/*
 * @Author: Auto Generated
 * @FilePath: /mall-fe/src/hooks/usePricePermissionQuery.ts
 * @Description: 商品价格显示权限查询 Hook
 *
 * 使用 React Query 在客户端获取商品价格显示权限
 * 用于控制商品列表、商品详情等页面是否显示价格
 */

import { useQuery } from '@tanstack/react-query';
import { showGoodsPricePermission } from 'api/user';
import { queryKeys } from 'libs/react-query';

/**
 * 使用 React Query 获取商品价格显示权限
 *
 * @param options - 查询选项
 * @param options.enabled - 是否启用查询（默认为 true）
 * @param options.initialData - 初始数据（可选，用于服务端渲染场景）
 *
 * @returns 返回查询结果对象，包含：
 *   - data: 价格权限数据
 *   - isLoading: 是否正在首次加载
 *   - isFetching: 是否正在获取数据（包括后台更新）
 *   - isError: 是否发生错误
 *   - error: 错误信息
 *   - refetch: 手动重新获取数据的函数
 *
 * @example
 * ```tsx
 * function ProductCard({ product }) {
 *   const { data: pricePermission, isLoading } = usePricePermissionQuery();
 *
 *   const showPrice = pricePermission?.showPrice ?? false;
 *
 *   return (
 *     <div>
 *       <h3>{product.name}</h3>
 *       {showPrice ? <span>¥{product.price}</span> : <span>请登录查看价格</span>}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePricePermissionQuery(options?: {
  enabled?: boolean;
  initialData?: PriceContextValue;
}) {
  const { enabled = true, initialData } = options || {};

  return useQuery({
    // 查询键，用于缓存和标识查询
    queryKey: queryKeys.pricePermission.detail(),

    // 查询函数，返回 Promise
    queryFn: async () => {
      try {
        const res = await showGoodsPricePermission({});

        if (res.code === 200 && res.data != null) {
          return res.data;
        }

        // 返回默认值：不显示价格
        return {
          showPrice: false,
          tip: '',
          showSellCount: false,
          showScore: false,
        };
      } catch (error) {
        console.error('获取商品价格权限失败:', error);
        // 返回默认值：不显示价格
        return {
          showPrice: false,
          tip: '',
          showSellCount: false,
          showScore: false,
        };
      }
    },

    // 是否启用查询
    enabled,

    // 初始数据（用于服务端渲染场景）
    initialData,

    // 数据在2分钟内被认为是新鲜的
    staleTime: 2 * 60 * 1000,
  });
}
